import AlertReference from './priceAlerts/AlertReference.jsx'
import { BottomLeftFeedback } from './ActionSuccessToast'
import { useCommandHandlers } from '../commands/CommandHandlers.jsx'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, Bell, CheckCircle2, X, XCircle } from 'lucide-react'
import { useNotifications } from '../hooks/useNotifications.jsx'
import { useAccountNicknames } from '../hooks/useAccountNicknames.jsx'
import { canonicalizeWorkspaceRouteValue } from '../lib/positionWorkspaceRoute.js'

const WARNING_ICON_CLASS = 'text-[#d99a2b]'
const WARNING_BORDER_CLASS = 'border-[#d99a2b]/50'
const WARNING_BADGE_CLASS = 'bg-[#d99a2b]'
const VIEWPORT_AUTO_DISMISS_MS = 5000
const VIEWPORT_AUTO_DISMISS_TONES = new Set(['success', 'info', 'warning'])

function orderDetailPath(symbol) {
  return canonicalizeWorkspaceRouteValue(null, symbol)
}

function isPartialSetupFailure(notification, details) {
  return notification?.type === 'order_batch'
    && notification?.action === 'STAGE_ORDERS'
    && details.some(row => row?.ok === true)
    && details.some(row => row?.ok !== true)
}

function repairSetupPath(notification) {
  const base = canonicalizeWorkspaceRouteValue(notification?.workspaceRoute, notification?.symbol)
  const separator = base.includes('?') ? '&' : '?'
  return `${base}${separator}repairSetup=missing`
}

function AccountLabel({ accountNumber }) {
  const { getNickname } = useAccountNicknames()
  if (!accountNumber) return <span>-</span>
  return <span className="text-[11px] font-semibold text-ink">{getNickname(accountNumber)}</span>
}

function notificationTime(notification) {
  return notification?.firedAt || notification?.createdAt || null
}

function formatNotificationTime(value, { includeDate = false, includeSeconds = false } = {}) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString([], {
    month: includeDate ? 'short' : undefined,
    day: includeDate ? 'numeric' : undefined,
    hour: 'numeric',
    minute: '2-digit',
    second: includeSeconds ? '2-digit' : undefined,
  })
}

function NotificationMeta({ notification, includeDate = false }) {
  const formatted = formatNotificationTime(notificationTime(notification), { includeDate, includeSeconds: notification.type === 'vwap_alert' })
  if (!formatted) return null
  return <span>Fired {formatted}</span>
}

function isSellNotification(notification = {}) {
  const text = String(notification.message || notification.title || '').trim().toUpperCase()
  return text.startsWith('SELL') || text.startsWith('SOLD')
}

function isCancelNotification(notification = {}) {
  if (notification.type === 'cancel') return true
  const text = `${notification.title || ''} ${notification.message || ''}`.toUpperCase()
  return text.includes('CANCELED') || text.includes('CANCELLED')
}

function notificationVisual(notification = {}) {
  if (notification.tone === 'error') {
    return {
      Icon: XCircle,
      iconClass: 'text-ember',
      borderClass: 'border-ember/40',
      badgeClass: 'bg-ember',
    }
  }
  if (notification.tone === 'warning' || isCancelNotification(notification)) {
    return {
      Icon: AlertTriangle,
      iconClass: WARNING_ICON_CLASS,
      borderClass: WARNING_BORDER_CLASS,
      badgeClass: WARNING_BADGE_CLASS,
    }
  }
  if (isSellNotification(notification)) {
    return {
      Icon: CheckCircle2,
      iconClass: 'text-ember',
      borderClass: 'border-ember/40',
      badgeClass: 'bg-ember',
    }
  }
  return {
    Icon: CheckCircle2,
    iconClass: 'text-forest',
    borderClass: 'border-forest/40',
    badgeClass: 'bg-forest',
  }
}

function isBellActiveNotification(notification = {}) {
  if (notification.cleared === true) return false
  if (notification.notificationState === 'cleared' || notification.notificationState === 'archived') return false
  if (notification.type === 'price_alert') return notification.dismissed !== true
  if (notification.tone === 'error') return notification.read !== true
  return notification.read !== true && notification.dismissed !== true
}

function NotificationToneIcon({ notification, size = 14 }) {
  const { Icon, iconClass } = notificationVisual(notification)
  return <Icon size={size} className={`mt-0.5 shrink-0 ${iconClass}`} data-testid="notification-tone-icon" />
}

function hasKeyValueDetails(details = []) {
  return details.length > 0 && details.every(row => (
    row &&
    Object.prototype.hasOwnProperty.call(row, 'label') &&
    Object.prototype.hasOwnProperty.call(row, 'value') &&
    !row.accountNumber &&
    !row.orderId &&
    !row.status
  ))
}

function numberOrNull(value) {
  if (value == null || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

function formatCurrency(value) {
  const num = numberOrNull(value)
  if (num == null) return '-'
  return num.toLocaleString([], {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatShares(value) {
  const num = numberOrNull(value)
  if (num == null) return '-'
  return Number.isInteger(num) ? num.toLocaleString() : num.toLocaleString([], { maximumFractionDigits: 2 })
}

function formatPercent(value) {
  const num = numberOrNull(value)
  if (num == null) return '-'
  return `${num.toLocaleString([], { maximumFractionDigits: 2 })}%`
}

function formatRiskReward(value) {
  const num = numberOrNull(value)
  if (num == null) return '-'
  return `1:${num.toFixed(2)}`
}

function setupSummaryFromDetailRows(details = []) {
  return (Array.isArray(details) ? details : [])
    .map(row => row?.setupSummary)
    .find(summary => summary && typeof summary === 'object') || null
}

function SetupMetric({ label, value, tone = 'default' }) {
  const toneClass = tone === 'risk'
    ? 'text-ember'
    : tone === 'success'
      ? 'text-forest'
      : 'text-ink'
  return (
    <div className="min-w-0">
      <div className="font-mono text-[9px] uppercase tracking-widest text-inkMuted">{label}</div>
      <div className={`mt-1 truncate text-xs font-semibold ${toneClass}`}>
        {value === null || value === undefined || value === '' ? '-' : value}
      </div>
    </div>
  )
}

function SetupSummarySection({ summary }) {
  if (!summary) return null
  const accounts = Array.isArray(summary.accounts) ? summary.accounts : []
  const setupTitle = [summary.pattern, summary.entryType].filter(Boolean).join(' · ')
  return (
    <div className="border-b border-border px-5 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">Setup</div>
          <div className="mt-1 text-sm font-semibold text-ink">{setupTitle || summary.symbol || 'Linked setup'}</div>
        </div>
        {(summary.setupStatus || summary.stage) && (
          <div className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">
            {[summary.setupStatus, summary.stage].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 md:grid-cols-4">
        <SetupMetric label="Entry" value={formatCurrency(summary.triggerPrice)} />
        <SetupMetric label="Stop" value={formatCurrency(summary.stopPrice)} tone="risk" />
        <SetupMetric label="Target" value={formatCurrency(summary.targetPrice)} tone="success" />
        <SetupMetric label="Size" value={formatPercent(summary.recommendedSizePct)} />
        <SetupMetric label="Shares" value={formatShares(summary.totalShares)} />
        <SetupMetric label="Position Size" value={formatCurrency(summary.positionValue)} />
        <SetupMetric label="Max Risk" value={formatCurrency(summary.maxRisk)} tone="risk" />
        <SetupMetric label="Risk/Reward" value={formatRiskReward(summary.riskReward)} />
      </div>
      {accounts.length > 0 && (
        <table className="mt-4 w-full table-fixed border-collapse text-xs">
          <colgroup>
            <col className="w-[34%]" />
            <col className="w-[18%]" />
            <col className="w-[24%]" />
            <col className="w-[24%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-border text-left font-mono text-[9px] uppercase tracking-widest text-inkMuted">
              <th className="py-1.5 pr-2">Account</th>
              <th className="py-1.5 pr-2">Shares</th>
              <th className="py-1.5 pr-2">Position</th>
              <th className="py-1.5">Risk</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((row, idx) => (
              <tr key={`${row.accountNumber || row.accountName || 'account'}-${idx}`} className="border-b border-border/60 last:border-0">
                <td className="py-1.5 pr-2 align-top">
                  {row.accountNumber ? <AccountLabel accountNumber={row.accountNumber} /> : <span className="text-[11px] text-ink">{row.accountName || '-'}</span>}
                </td>
                <td className="py-1.5 pr-2 align-top font-mono text-[11px] text-inkMuted">{formatShares(row.shares)}</td>
                <td className="py-1.5 pr-2 align-top font-mono text-[11px] text-inkMuted">{formatCurrency(row.positionValue)}</td>
                <td className="py-1.5 align-top font-mono text-[11px] text-ember">{formatCurrency(row.maxRisk)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function NotificationDetail({ notification, onClose }) {
  useEffect(() => {
    if (!notification) return undefined
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [notification, onClose])

  if (!notification) return null
  if (notification.type === 'price_alert') return <AlertReference key={notification.id} notification={notification} onClose={onClose}/>
  const isVwap = notification.type === 'vwap_alert'
  const details = notification.details?.length ? notification.details : isVwap ? [{label:'Symbol',value:notification.symbol || '—'}] : []
  const keyValueDetails = hasKeyValueDetails(details)
  const setupSummary = notification.setupSummary || setupSummaryFromDetailRows(details)
  const canRepairMissingOrders = isPartialSetupFailure(notification, details)
  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/45 px-4" onClick={onClose}>
      <div
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-border bg-canvas shadow-2xl"
        data-testid="notification-detail-dialog"
        onClick={e => e.stopPropagation()}
      >
        <div className="shrink-0 flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">{isVwap ? 'AVWAP alert details' : 'Order Details'}</div>
            <div className="mt-1 text-lg font-semibold text-ink">{notification.title}</div>
            <div className="mt-0.5 font-mono text-[10px] text-inkMuted">{notification.message}</div>
            <div className="mt-1 font-mono text-[10px] text-inkMuted">
              <NotificationMeta notification={notification} includeDate />
            </div>
          </div>
          <button className="rounded-lg p-1.5 text-inkMuted hover:bg-canvas2 hover:text-ink" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto" data-testid="notification-detail-scroll">
          <SetupSummarySection summary={setupSummary} />
          <div className="px-5 py-4">
            {keyValueDetails ? (
              <dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-xs">
                {details.map((row, idx) => (
                  <div key={`${row.label}-${idx}`} className="contents">
                    <dt className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">{row.label}</dt>
                    <dd className="break-all text-inkMuted">{row.value || '-'}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <table className="w-full table-fixed border-collapse text-xs">
                <colgroup>
                  <col className="w-[16%]" />
                  <col className="w-[13%]" />
                  <col className="w-[19%]" />
                  <col className="w-[22%]" />
                  <col className="w-[14%]" />
                  <col className="w-[16%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-widest text-inkMuted">
                    <th className="py-2 pr-2">Account</th>
                    <th className="py-2 pr-2">Role</th>
                    <th className="py-2 pr-2">Order</th>
                    <th className="py-2 pr-2">Details</th>
                    <th className="py-2 pr-2">Status</th>
                    <th className="py-2">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {details.map((row, idx) => (
                    <tr key={`${row.accountNumber}-${row.orderId}-${idx}`} className="border-b border-border/70 last:border-0">
                      <td className="py-2 pr-2 align-top">
                        <AccountLabel accountNumber={row.accountNumber} />
                      </td>
                      <td className="py-2 pr-2 align-top text-xs text-inkMuted">{row.role || '-'}</td>
                      <td className="break-all py-2 pr-2 align-top font-mono text-[11px] text-inkMuted">
                        {row.orderId || '-'}
                      </td>
                      <td className="py-2 pr-2 align-top text-[11px] text-inkMuted">
                        <div>{row.summary || '-'}</div>
                        {(row.duration || row.placedAt) && (
                          <div className="mt-0.5 font-mono text-[9px] text-inkMuted/80">
                            {[row.duration, row.placedAt].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className={`break-words py-2 pr-2 align-top font-mono text-[10px] ${row.ok ? 'text-forest' : 'text-ember'}`}>
                        {row.status || '-'}
                      </td>
                      <td className="break-words py-2 align-top text-xs text-inkMuted">{row.message || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        {(notification.workspaceRoute || notification.symbol) && (
          <div className="shrink-0 flex justify-end gap-2 border-t border-border px-5 py-3">
            {canRepairMissingOrders && (
              <Link
                to={repairSetupPath(notification)}
                className="rounded-lg border border-ember/40 bg-ember/10 px-3 py-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-ember hover:bg-ember/20"
                onClick={onClose}
              >
                Repair Missing Orders
              </Link>
            )}
            <Link
              to={canonicalizeWorkspaceRouteValue(notification.workspaceRoute, notification.symbol)}
              className="rounded-lg border border-ink/20 px-3 py-2 font-mono text-[10px] font-semibold uppercase tracking-widest text-ink hover:bg-canvas2"
              onClick={onClose}
            >
              Open Workspace
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export function NotificationCard({ notification, onOpen, onDismiss, showMeta = true, testId = 'order-notification-toast' }) {
  const Content = onOpen ? 'button' : 'div'
  const { borderClass } = notificationVisual(notification)
  return (
    <div
      className={`w-[340px] animate-notification-toast-in rounded-xl border bg-canvas shadow-xl ${borderClass}`}
      data-testid={testId}
    >
      {notification.type === 'price_alert' && <Link to={canonicalizeWorkspaceRouteValue(notification.workspaceRoute, notification.symbol)} className="mx-3 mt-2 inline-block text-xs font-semibold underline underline-offset-2">{notification.symbol}</Link>}
      <Content
        className="flex w-full items-start gap-3 px-3 py-3 text-left"
        onClick={onOpen}
      >
        <NotificationToneIcon notification={notification} size={16} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-ink">{notification.title}</div>
          {notification.message && <div className="mt-0.5 font-mono text-[11px] text-inkMuted">{notification.message}</div>}
          {showMeta && <div className="mt-1 font-mono text-[10px] text-inkMuted">
            <NotificationMeta notification={notification} />
          </div>}
        </div>
      </Content>
      <button
        className="absolute right-2 top-2 rounded p-1 text-inkMuted hover:bg-canvas2 hover:text-ink"
        onClick={onDismiss}
        title="Dismiss"
      >
        <X size={12} />
      </button>
    </div>
  )
}

export function NotificationViewport() {
  const navigate = useNavigate()
  const { notifications, centerOpen, dismissNotificationPopup, markNotificationRead } = useNotifications()
  const [detail, setDetail] = useState(null)
  const autoDismissTimersRef = useRef(new Map())
  const pending = notifications.filter(n => (
    !n.popupDismissed &&
    !n.dismissed &&
    !n.read &&
    !n.cleared
  ))
  const visible = centerOpen ? [] : pending.slice(0, 3)

  useEffect(() => {
    if (!centerOpen) return
    // The bell owns presentation while open. Consume popups, retaining unread history.
    pending.forEach(notification => dismissNotificationPopup(notification.id))
  }, [centerOpen, pending, dismissNotificationPopup])

  useEffect(() => {
    const visibleIds = new Set(visible.map(notification => notification.id))
    for (const [id, timer] of autoDismissTimersRef.current.entries()) {
      if (visibleIds.has(id)) continue
      clearTimeout(timer)
      autoDismissTimersRef.current.delete(id)
    }
    visible.forEach((notification) => {
      if (
        !notification.id ||
        notification.sticky === true ||
        !VIEWPORT_AUTO_DISMISS_TONES.has(notification.tone) ||
        autoDismissTimersRef.current.has(notification.id)
      ) {
        return
      }
      const timer = setTimeout(() => {
        autoDismissTimersRef.current.delete(notification.id)
        dismissNotificationPopup(notification.id)
      }, notification.type === 'setup_import' || notification.tone !== 'success' ? 7000 : VIEWPORT_AUTO_DISMISS_MS)
      autoDismissTimersRef.current.set(notification.id, timer)
    })
  }, [visible, dismissNotificationPopup])

  useEffect(() => () => {
    for (const timer of autoDismissTimersRef.current.values()) {
      clearTimeout(timer)
    }
    autoDismissTimersRef.current.clear()
  }, [])

  return (
    <>
      <BottomLeftFeedback><div className="flex flex-col gap-2">
        {visible.filter(notification => notification.tone === 'success').map(notification => (
          <div key={notification.id} className="relative">
            <NotificationCard
              notification={notification}
              onOpen={() => {
                markNotificationRead(notification.id)
                setDetail(notification)
              }}
              onDismiss={(e) => {
                e.stopPropagation()
                dismissNotificationPopup(notification.id)
              }}
            />
          </div>
        ))}
      </div></BottomLeftFeedback>
      <div className="fixed right-5 top-[68px] z-[9997] flex flex-col gap-2">
        {visible.filter(notification => notification.tone !== 'success').map(notification => (
          <div key={notification.id} className="relative">
            <NotificationCard
              notification={notification}
              onOpen={() => {
                markNotificationRead(notification.id)
                setDetail(notification)
              }}
              onDismiss={(e) => {
                e.stopPropagation()
                dismissNotificationPopup(notification.id)
              }}
            />
          </div>
        ))}
      </div>
      <NotificationDetail notification={detail} onClose={() => setDetail(null)} />
    </>
  )
}

export function NotificationButton() {
  const navigate = useNavigate()
  const { notifications, centerOpen: open, setCenterOpen: setOpen, dismissNotification, markNotificationRead, clearNotificationHistory } = useNotifications()
  useCommandHandlers({ 'app.notifications.open': () => { setOpen(true) } })
  const [view, setView] = useState('active')
  const [detail, setDetail] = useState(null)
  const dropdownRef = useRef(null)
  useEffect(() => () => setOpen(false), [setOpen])
  const activeNotifications = notifications.filter(isBellActiveNotification)
  const historyNotifications = notifications.filter(n => !isBellActiveNotification(n))
  const visibleNotifications = view === 'history' ? historyNotifications : activeNotifications
  const issueCount = activeNotifications.filter(n => n.tone === 'error').length
  const unreadCount = activeNotifications.filter(n => !n.read).length

  useEffect(() => {
    if (!open) return undefined
    const handleMouseDown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, setOpen])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="relative flex h-7 w-7 md:h-8 md:w-8 items-center justify-center rounded-full border border-border bg-canvas2 text-inkMuted transition-colors hover:border-ink/30 hover:text-ink"
        onClick={() => setOpen(o => !o)}
        aria-label="Order notifications" data-command-label="Order notifications" data-command-id="app.notifications.open"
      >
        <Bell size={14} />
        {(issueCount > 0 || unreadCount > 0) && (
          <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border border-[#0b5d40] bg-[#16835b] px-1 text-center font-mono text-[9px] font-semibold !text-white">
            {issueCount || unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-50 w-[360px] rounded-xl border border-border bg-canvas shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-inkMuted">Notifications</div>
            {activeNotifications.length > 0 && (
              <button className="font-mono text-[9px] uppercase tracking-widest text-inkMuted hover:text-ink" onClick={clearNotificationHistory}>
                Clear
              </button>
            )}
          </div>
          <div className="flex border-b border-border px-2 py-1">
            {['active', 'history'].map(option => (
              <button
                key={option}
                type="button"
                className={`rounded px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${
                  view === option ? 'bg-canvas2 text-ink' : 'text-inkMuted hover:text-ink'
                }`}
                onClick={() => setView(option)}
              >
                {option}
              </button>
            ))}
          </div>
          <div className="max-h-[420px] overflow-auto py-1">
            {visibleNotifications.length === 0 ? (
              <div className="px-3 py-6 text-center font-mono text-[11px] text-inkMuted">
                {view === 'history' ? 'No notification history' : 'No active notifications'}
              </div>
            ) : visibleNotifications.map(notification => {
              return (
                <div key={notification.id} className="group flex items-start gap-2 px-3 py-2 hover:bg-canvas2">
                  <NotificationToneIcon notification={notification} />
                  {notification.type === 'price_alert' && <Link to={canonicalizeWorkspaceRouteValue(notification.workspaceRoute, notification.symbol)} onClick={() => { markNotificationRead(notification.id); setOpen(false) }} className="text-xs font-semibold underline underline-offset-2">{notification.symbol}</Link>}
                  <button
                    className="min-w-0 flex-1 text-left"
                    onClick={() => {
                      markNotificationRead(notification.id)
                      setDetail(notification)
                      setOpen(false)
                    }}
                  >
                    <div className="truncate text-sm font-semibold text-ink">{notification.title}</div>
                    <div className="truncate font-mono text-[10px] text-inkMuted">{notification.message}</div>
                    <div className="truncate font-mono text-[10px] text-inkMuted">
                      <NotificationMeta notification={notification} includeDate />
                    </div>
                  </button>
                  {view === 'active' && (
                    <button
                      className="rounded p-1 text-inkMuted opacity-0 hover:bg-canvas hover:text-ink group-hover:opacity-100"
                      onClick={() => dismissNotification(notification.id)}
                      title="Dismiss"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
      <NotificationDetail notification={detail} onClose={() => setDetail(null)} />
    </div>
  )
}

export const OrderNotificationViewport = NotificationViewport
export const OrderNotificationButton = NotificationButton
